import { jobLists } from "character";
import FormComponents from "components/forms/FormComponents";
import JobIcon from "components/job-icon/JobIcon";
import Link from "components/link/Link";
import NoCharacter from "components/no-character/NoCharacter";
import RelicCheckbox from "components/relic-checkbox/RelicCheckbox";
import { items } from "data-files/items";
import { quests } from "data-files/quests";
import { GameData, InputType, Job, LinkType, RelicData, View } from "enums";
import manager from "managers/app";
import { ViewRelicInfo } from "managers/types/relic-types";
import { Component } from "react";
import { connect } from "react-redux";
import t from "translations/translator";
import { AppState } from "types/app";
import { Character } from "types/data";
import ContentHeader from "views/page-parts/content/content-header/ContentHeader";
import DetailsSection from "views/page-parts/content/details-section/DetailsSection";
import Obtained from "views/page-parts/content/obtained/Obtained";
import OverviewTable from "views/page-parts/content/overview-table/OverviewTable";
import { Requirement } from "views/page-parts/content/relic-requirements/types";
import { ViewToolCosmicCosmicProps } from "views/relics/tools/cosmic/cosmic/types";
import { TaskInfo } from "views/type";
import "./ToolCosmicCosmic.css";

const mapStateToProps = (state:AppState):ViewToolCosmicCosmicProps.Redux => {
	return {
		lang: state.userInfo.options.lang,
		gameLang: state.userInfo.options.gameLang,
		userInfo: state.userInfo,
		hideCompletedSteps: state.userInfo.options.hideCompletedSteps,
		hideCompletedTasks: state.userInfo.options.hideCompletedTasks,
		showJob: state.userInfo.options.job,
	};
};

class ToolCosmicCosmic extends Component<ViewToolCosmicCosmicProps.Component> {
	private requirements:Requirement[] = [];
	private importantNPCs:GameData.NPC[] = [];
	private relicInfo:ViewRelicInfo = {
		jobs: jobLists.relic[RelicData.Group.TOOL_COSMIC],
		type: RelicData.Type.TOOL,
		relic: RelicData.Group.TOOL_COSMIC,
		part: RelicData.Part.TOOL_COSMIC,
		steps: [
			RelicData.Step.PROTOTYPE_01,
			RelicData.Step.PROTOTYPE_02,
			RelicData.Step.PROTOTYPE_03,
			RelicData.Step.PROTOTYPE_04,
			RelicData.Step.PROTOTYPE_05,
			RelicData.Step.PROTOTYPE_06,
			RelicData.Step.PROTOTYPE_07,
			RelicData.Step.PROTOTYPE_08,
			RelicData.Step.COSMIC,
		],
		stepPlaceholders: { [RelicData.Step.PROTOTYPE_01]: { NPC: GameData.NPC.RESEARCHINGWAY } },
		next: {
			part: RelicData.Part.TOOL_STELLAR,
			view: View.RELIC_TOOLS_COSMIC_STELLAR,
		},
		previous: null,
	};

	constructor(props:ViewToolCosmicCosmicProps.Component){
		super(props);
		this.requirements = this.getRequirements();
		this.importantNPCs = this.getImportantNPCs();
	}

	render():JSX.Element{
		const character = manager.data.getActiveCharacter();
		if(character === null){ return <NoCharacter />; }

		const completeSteps = manager.content.getCompleteSteps(character.progress, this.relicInfo);

		return (
			<div id="tool-cosmic-cosmic" className="relic">
				<ContentHeader requirements={this.requirements} importantNPCs={this.importantNPCs} relicInfo={this.relicInfo}>
					<p>{t.t("Complete")} <Link
						type={LinkType.LODESTONE}
						lodestoneType={quests[GameData.Quest.COSMIC_HOMECOMING].type}
						text={GameData.Quest.COSMIC_HOMECOMING}
						lodestoneID={quests[GameData.Quest.COSMIC_HOMECOMING].lodestoneID}
					/></p>
				</ContentHeader>
				<div id="relic-checklist">
					<OverviewTable character={character} completeSteps={completeSteps} relicInfo={this.relicInfo} />
				</div>
				<div id="relic-details">
					<h1>{t.t("Details")}</h1>
					{this.props.hideCompletedSteps && completeSteps.allComplete ? t.t("All Steps Complete") : null }
					<DetailsSection
						title={RelicData.Step.PROTOTYPE_01}
						placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.PROTOTYPE_01]}
						id={RelicData.Step.PROTOTYPE_01}
						stepComplete={completeSteps[RelicData.Step.PROTOTYPE_01]}
					>
						<p>{t.t("Complete")}<Link
							type={LinkType.LODESTONE}
							lodestoneType={quests[GameData.Quest.COSMIC_HOMECOMING].type}
							text={quests[GameData.Quest.COSMIC_HOMECOMING].name}
							lodestoneID={quests[GameData.Quest.COSMIC_HOMECOMING].lodestoneID}
						/></p>
						<p>{t.t("The remaining Prototype v0.1 tools can be obtained from %NPC% and selecing 'Inquire about cosmic tools'", this.relicInfo.stepPlaceholders[RelicData.Step.PROTOTYPE_01])}</p>
					</DetailsSection>
					{
						this.relicInfo.steps.map((step, index) => {
							if(index === 0){ return null; } // Skip first step

							let row = "odd-row";

							return <DetailsSection title={step} id={step} placeholders={this.relicInfo.stepPlaceholders[step]} stepComplete={completeSteps[step]} key={step}>
								<p>{t.t("It is recommended to work on 1 relic at a time, as you get a global research bonus when you have a number of %PART% tools", { PART: this.relicInfo.part})}</p>
								<div className="table-wrapper">
									<table className="table">
										<thead><tr>
											<th>{t.t("Job")}</th>
											<th>{t.t("Task")}</th>
											<th>{t.t("Found")}</th>
											<th>{t.t("Quantity")}</th>
											<th>{t.t("Inventory")}</th>
											<th>{t.t("To Obtain")}</th>
											<th>{t.t("Done")}</th>
										</tr></thead>
										<tbody>
											{
												this.relicInfo.jobs.map((job) => {
													if(this.props.showJob !== null && this.props.showJob !== job){ return null; }
													const stepComplete = character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, job);

													if(this.props.hideCompletedSteps && stepComplete){ return null; }

													const taskInfo = this.getTasksInfo(step, character, job);

													const tableRows = taskInfo.map((thisTask, taskIndex) => {
														if(this.props.hideCompletedTasks && thisTask.complete){ return null; }

														const targetItem = thisTask.target as GameData.Item;

														const inventory = manager.data.getInventoryStatus({
															item: targetItem,
															relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, thisTask.task],
															job: job,
														});

														const showJobIcon = manager.content.shouldShowJobIcon(this.props.hideCompletedTasks, taskInfo, taskIndex);
														const rowSpan = manager.content.getRowSpan(this.props.hideCompletedTasks, taskInfo, taskIndex);

														return <tr key={`${job}-${step}-${thisTask.task}`} className={row}>
															{showJobIcon ? <td rowSpan={rowSpan}><JobIcon job={job} /></td> : null}
															<td>{t.t(targetItem)} ({t.t(job)})</td>
															<td><Obtained obtained={items[targetItem].obtained} qtyNeeded={inventory.total} /></td>
															<td>{inventory.qtyPerJob.toLocaleString()}</td>
															<td>
																<FormComponents.TextInput
																	label={{ hide: true }}
																	input={{ type: InputType.NUMBER, value: inventory.inventory, dataAttributes: { item: targetItem }, events: { onChange: manager.content.inventoryUpdated }}}
																/>
															</td>
															<td>{inventory.toObtain.toLocaleString()}</td>
															<td><RelicCheckbox character={character} job={job} relicInfo={this.relicInfo} step={step} task={thisTask.task} /></td>
														</tr>;
													});
													row = row === "odd-row" ? "even-row" : "odd-row";
													return tableRows;
												})
											}
											{
												completeSteps[step]
													? <tr className="all-complete"><td colSpan={7}><p>{t.t("All Complete")}</p></td></tr>
													: null
											}
										</tbody>
									</table>
								</div>
							</DetailsSection>;
						})
					}
				</div>
			</div>
		);
	}

	componentDidMount():void{
		manager.view.changeComplete();
	}

	private getRequirements():Requirement[]{
		return [
			{ type: quests[GameData.Quest.ENDWALKER].type, text: GameData.Quest.ENDWALKER, lodestoneID: quests[GameData.Quest.ENDWALKER].lodestoneID },
		];
	}

	private getImportantNPCs():GameData.NPC[]{
		return [GameData.NPC.RESEARCHINGWAY];
	}

	private getTasksInfo(step:RelicData.Step, character:Character, job:Job):TaskInfo[]{
		const tasksInfo:TaskInfo[] = [];
		const tasks = [RelicData.Task.TASK_1, RelicData.Task.TASK_2, RelicData.Task.TASK_3, RelicData.Task.TASK_4];
		const hasTask2 = [RelicData.Step.PROTOTYPE_04, RelicData.Step.PROTOTYPE_05, RelicData.Step.PROTOTYPE_06, RelicData.Step.PROTOTYPE_07, RelicData.Step.PROTOTYPE_08, RelicData.Step.COSMIC];
		const hasTask3 = [RelicData.Step.PROTOTYPE_06, RelicData.Step.PROTOTYPE_07, RelicData.Step.PROTOTYPE_08, RelicData.Step.COSMIC];
		const hasTask4 = [RelicData.Step.PROTOTYPE_08, RelicData.Step.COSMIC];

		tasks.forEach((task) => {
			if(task === RelicData.Task.TASK_2 && !hasTask2.includes(step)){ return; }
			if(task === RelicData.Task.TASK_3 && !hasTask3.includes(step)){ return; }
			if(task === RelicData.Task.TASK_4 && !hasTask4.includes(step)){ return; }

			const target = manager.relics.getTaskItem(step, task, job);
			const complete = character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, task, job);
			tasksInfo.push({ task, complete, target });
		});
		return tasksInfo;
	}
}
export default connect(mapStateToProps)(ToolCosmicCosmic);
